import * as coreCommon from 'assets/core/js/common'
import dataLayer, { type PageData } from 'assets/core/js/module/dataLayer'

declare global {
  interface Window {
    pageData: PageData
  }
}

interface PartialConfig {
  type: string
  url: string
  partials?: Record<string, HTMLElement>
}

interface Partials {
  [key: string]: {
    elements: Record<string, HTMLElement>
    config: PartialConfig
  }
}

export const PartialsEvents = {
  ERROR_UPDATE: 'dca.partial.error',
  PRE_UPDATE: 'dca.partial.pre',
  POST_UPDATE: 'dca.partial.post',
}

export interface PartialEventDetail {
  data?:
    | null
    | object
    | {
        accommodationsData: Record<string, Record<string, string[] | number>>
        accommodationsOrder: number[]
      }
  partial?: undefined | HTMLElement
}

const partials: Partials = {}

export default {
  init(context: string, elements: Record<string, HTMLElement>, config: PartialConfig) {
    if (!partials[context]) {
      if (!config.type) {
        return
      }

      if (!elements) {
        elements = {}
      }

      partials[context] = {
        elements,
        config,
      }
    }

    return partials[context]
  },

  get(context: string) {
    return partials[context]
  },

  remove(context: string) {
    if (partials[context]) {
      delete partials[context]

      return true
    }

    return false
  },

  update(context: string, parameters: Record<string, string>) {
    if (!partials[context]) {
      return
    }

    if (parameters) {
      switch (partials[context]?.config.type) {
        case 'url': {
          if (!partials[context]?.config.url) {
            return
          }

          const elements = this.get(context)?.elements || []

          Object.keys(elements).forEach((key) => {
            partials[context]?.elements[key]?.setAttribute('data-partial-status', 'updating')
            partials[context]?.elements[key]?.dispatchEvent(
              new CustomEvent(PartialsEvents.PRE_UPDATE, {
                detail: {
                  partial: partials[context]?.elements[key],
                },
              })
            )
          })

          const qs = new URLSearchParams(parameters).toString()

          const xhr = coreCommon.createXhr('GET', `${partials[context]?.config.url}${partials[context]?.config.url.includes('?') ? '&' : '?'}${qs}`)

          xhr.setRequestHeader('Content-type', 'application/json')
          xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
              const response: Record<string, Record<string, unknown>> = JSON.parse(xhr.response as string)

              const elements = this.get(context)?.elements || []

              Object.keys(elements).forEach((key) => {
                const eventDetail: PartialEventDetail = {
                  data: null,
                  partial: undefined,
                }

                const res = response[key]

                if (res) {
                  if (res?.html && partials[context]?.elements[key]) {
                    const node = coreCommon.createNodeFromHTML((res?.html as string).trim())

                    if (!node) {
                      return
                    }

                    partials[context].elements[key].innerHTML = node.innerHTML

                    eventDetail.partial = partials[context]?.elements[key]
                  }

                  if (response[key]?.data) {
                    eventDetail.data = response[key]?.data
                  }

                  // @ts-expect-error argument is not unknown
                  dataLayer.pushForJsonResponse(response[key]?.tagDatas)
                }

                partials[context]?.elements[key]?.removeAttribute('data-partial-status')
                partials[context]?.elements[key]?.dispatchEvent(
                  new CustomEvent<PartialEventDetail>(PartialsEvents.POST_UPDATE, {
                    detail: eventDetail,
                    bubbles: true,
                  })
                )
              })
            } else if (xhr.status !== 200) {
              const elements = this.get(context)?.elements || []

              Object.keys(elements).forEach((key) => {
                partials[context]?.elements[key]?.dispatchEvent(
                  new CustomEvent<PartialEventDetail>(PartialsEvents.ERROR_UPDATE, {
                    detail: {
                      partial: partials[context]?.elements[key],
                    },
                  })
                )
              })
            }
          }

          xhr.send()
          break
        }

        default:
          break
      }
    }
  },
}
